import React, { forwardRef, RefObject, useEffect, useMemo, useRef } from 'react';
import {
  scaleLinear as d3scaleLinear,
  ScaleLinear as d3ScaleLinear,
  scaleBand as d3scaleBand,
  ScaleBand as d3ScaleBand,
  sum as d3sum,
} from 'd3';

import { useTheme, useMessageFormatter } from '@redsift/design-system';
import intlMessages from './intl';

import { AnyScale, CategoryData } from '../../types';
import { BarChartProps, BarChartState } from './types';
import { StyledBarChart } from './styles';
import { Bar, BarOrientation } from '../Bar';
import { Axis, AxisVariant } from '../Axis';
import { useFormatCategoricalData } from '../../hooks';
import { sizeToDimension } from './utils';
import { BarChartContext } from './context';
import { ChartTypeContext } from '../ChartContainer/context';

interface RenderedOrdinalBarChartProps extends Required<Pick<BarChartProps, 'data'>>, Omit<BarChartProps, 'data'> {}

export const RenderedOrdinalBarChart = forwardRef<HTMLDivElement, RenderedOrdinalBarChartProps>((props, ref) => {
  const {
    areXLabelsRotated,
    barProps,
    caping,
    className,
    data: propsData,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    dataType: _dataType,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    direction: _direction,
    disableAnimations,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    emptyComponent: _emptyComponent,
    id,
    isBarSelected,
    getBarAnchorProps,
    labelDecorator,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    legendDecorator: _legendDecorator,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    legendProps: _legendProps,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    legendVariant: _legendVariant,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    localeText: _localeText,
    margins: propsMargins,
    onBarClick,
    orientation,
    others,
    size,
    sortingMethod,
    barRole,
    colorTheme,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    tooltipDecorator: _tooltipDecorator,
    tooltipVariant,
    xAxisVariant: propsXAxisVariant,
    xAxisPlacement,
    xAxisTickFormat,
    xAxisTickPadding,
    xAxisTickRotation,
    xAxisTickSize,
    xAxisTickValues,
    yAxisVariant: propsYAxisVariant,
    yAxisPlacement,
    yAxisTickFormat,
    yAxisTickPadding,
    yAxisTickRotation,
    yAxisTickSize,
    yAxisTickValues,
    ...forwardedProps
  } = props;

  const cache = useRef<CategoryData>();
  const theme = useTheme();

  const format = useMessageFormatter(intlMessages);

  const { data, colorScale } = useFormatCategoricalData({
    data: propsData as CategoryData,
    colorTheme: colorTheme!,
    sortingMethod: sortingMethod!,
    caping,
    others,
    theme,
  });

  useEffect(() => {
    cache.current = data;
  });

  const isHorizontal = orientation === BarOrientation.horizontal;
  const xAxisVariant = propsXAxisVariant
    ? propsXAxisVariant
    : isHorizontal
    ? AxisVariant.default
    : AxisVariant.tickValue;
  const yAxisVariant = propsYAxisVariant ? propsYAxisVariant : isHorizontal ? AxisVariant.none : AxisVariant.default;

  const chartDimensions = sizeToDimension(size!);
  const width = chartDimensions.width;
  const height = chartDimensions.height;
  const margins = { top: 16, left: isHorizontal ? 16 : 64, right: 16, bottom: 32, ...propsMargins };
  const chartHeight = height - margins.top - margins.bottom;
  const chartWidth = width - margins.left - margins.right;
  // When barRole is 'option' (selection mode), keep 0-value bars visible on the axis
  // so that unselected bars remain in place (greyed out) instead of disappearing.
  const isSelectionMode = barRole === 'option';
  const visibleData = isSelectionMode ? data : data.filter((datum) => datum.value);
  const numberOfRows = visibleData.length;
  const gap = barProps?.gap ?? 5;

  const barHeight = chartHeight / numberOfRows - gap;
  const barWidth = chartWidth / numberOfRows - gap;
  const total = d3sum(data, (d) => d.value);

  const scale = useMemo<d3ScaleLinear<number, number>>(
    () =>
      d3scaleLinear()
        .domain([0, Math.max(...data.map(({ value }) => value))])
        .range(isHorizontal ? [0, chartWidth] : [chartHeight, 0]),
    [data, size, isHorizontal]
  );
  const scaleCategory = useMemo<d3ScaleBand<string>>(
    () =>
      d3scaleBand()
        .domain(visibleData.map((datum) => datum.key))
        .range([0, isHorizontal ? chartHeight : chartWidth]),
    [data, size, isSelectionMode, isHorizontal]
  );

  const bars = visibleData;

  return (
    <ChartTypeContext.Provider value="bar">
      <BarChartContext.Provider value={{} as BarChartState}>
        <StyledBarChart
          mode={typeof onBarClick === 'function' || typeof getBarAnchorProps === 'function' ? 'interactive' : 'static'}
          definition={format('definition-ordinal', { barLength: bars.length })}
          xAxisDefinition={
            isHorizontal
              ? format('x-axis-numbers-definition', {
                  numAxis: xAxisPlacement === 'both' ? 2 : 1,
                  start: scale.domain()[0],
                  end: scale.domain()[1],
                })
              : format('x-axis-categories-definition', { numAxis: xAxisPlacement === 'both' ? 2 : 1 })
          }
          yAxisDefinition={
            isHorizontal
              ? format('y-axis-categories-definition', { numAxis: yAxisPlacement === 'both' ? 2 : 1 })
              : format('y-axis-numbers-definition', {
                  numAxis: yAxisPlacement === 'both' ? 2 : 1,
                  start: scale.domain()[0],
                  end: scale.domain()[1],
                })
          }
          dataTableRepresentation={{
            header: (
              <thead>
                <tr>
                  <th scope="col">Key</th>
                  <th scope="col">Value</th>
                </tr>
              </thead>
            ),
            body: (
              <tbody>
                {bars.map((datum) => (
                  <tr key={datum.key}>
                    <th scope="row">{datum.key}</th>
                    <td>{datum.value}</td>
                  </tr>
                ))}
              </tbody>
            ),
          }}
          {...forwardedProps}
          id={id}
          className={className}
          ref={ref as RefObject<HTMLDivElement>}
        >
          <svg
            width={width}
            height={height}
            aria-label={
              typeof onBarClick === 'function' || typeof getBarAnchorProps === 'function'
                ? format('interactive-chart')
                : format('static-chart')
            }
            aria-hidden="false"
          >
            <g transform={`translate(${margins.left},${margins.top})`}>
              {['bottom', 'both'].includes(xAxisPlacement!) ? (
                <Axis
                  disableAnimations={disableAnimations}
                  position="bottom"
                  length={chartWidth}
                  scale={isHorizontal ? (scale as AnyScale) : (scaleCategory as AnyScale)}
                  x={0}
                  y={chartHeight}
                  tickValues={xAxisTickValues ?? 4}
                  tickPadding={xAxisTickPadding}
                  tickRotation={xAxisTickRotation}
                  tickSize={xAxisTickSize}
                  tickFormat={xAxisTickFormat}
                  variant={xAxisVariant}
                />
              ) : null}
              {['top', 'both'].includes(xAxisPlacement!) ? (
                <Axis
                  disableAnimations={disableAnimations}
                  position="top"
                  length={chartWidth}
                  scale={isHorizontal ? (scale as AnyScale) : (scaleCategory as AnyScale)}
                  x={0}
                  y={0}
                  tickValues={xAxisTickValues ?? 4}
                  tickPadding={xAxisTickPadding}
                  tickRotation={xAxisTickRotation ? xAxisTickRotation : areXLabelsRotated ? -45 : 0}
                  tickSize={xAxisTickSize}
                  tickFormat={xAxisTickFormat}
                  variant={xAxisVariant}
                />
              ) : null}
              {['left', 'both'].includes(yAxisPlacement!) ? (
                <Axis
                  disableAnimations={disableAnimations}
                  position="left"
                  length={chartHeight}
                  scale={isHorizontal ? (scaleCategory as AnyScale) : (scale as AnyScale)}
                  x={0}
                  y={chartHeight}
                  tickValues={yAxisTickValues ?? 4}
                  tickPadding={yAxisTickPadding}
                  tickRotation={yAxisTickRotation}
                  tickSize={yAxisTickSize}
                  tickFormat={yAxisTickFormat}
                  variant={yAxisVariant}
                />
              ) : null}
              {['right', 'both'].includes(yAxisPlacement!) ? (
                <Axis
                  disableAnimations={disableAnimations}
                  position="right"
                  length={chartHeight}
                  scale={isHorizontal ? (scaleCategory as AnyScale) : (scale as AnyScale)}
                  x={chartWidth}
                  y={chartHeight}
                  tickValues={yAxisTickValues ?? 4}
                  tickPadding={yAxisTickPadding}
                  tickRotation={yAxisTickRotation}
                  tickSize={yAxisTickSize}
                  tickFormat={yAxisTickFormat}
                  variant={yAxisVariant}
                />
              ) : null}
            </g>
            <g transform={`translate(${margins.left},${margins.top})`}>
              {bars.map((datum, index) => {
                const percent = datum.value / total;
                const to = {
                  data: {
                    ...datum,
                    percent,
                  },
                };
                const from = cache.current ? { data: cache.current[index] } : undefined;

                return (
                  <Bar
                    color={colorScale?.(to.data.key)!}
                    data={to}
                    dataset={bars}
                    disableAnimations={disableAnimations}
                    gap={gap}
                    id={`id${id}__bar-${index}`}
                    index={index}
                    isSelected={Boolean(isBarSelected!(to))}
                    key={`bar _${index}`}
                    labelDecorator={labelDecorator}
                    maxHeight={isHorizontal ? undefined : chartHeight}
                    anchorProps={getBarAnchorProps ? getBarAnchorProps(to) : undefined}
                    onClick={onBarClick}
                    orientation={orientation}
                    previousData={from!}
                    scale={scale!}
                    scalePosition={scaleCategory}
                    tooltipVariant={tooltipVariant}
                    {...barProps}
                    width={
                      isHorizontal
                        ? undefined
                        : barProps?.width && typeof barProps.width === 'number'
                        ? barProps.width
                        : barWidth
                    }
                    height={
                      isHorizontal
                        ? barProps?.height && typeof barProps.height === 'number'
                          ? barProps.height
                          : barHeight
                        : undefined
                    }
                    role={barRole}
                  />
                );
              })}
            </g>
          </svg>
        </StyledBarChart>
      </BarChartContext.Provider>
    </ChartTypeContext.Provider>
  );
});
